import React from 'react';
import { Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ModalHeader from 'components/Modal/ModalHeader';
import SearchBar from 'components/Fields/ReduxForm/SearchBar';
import classNames from 'classnames/bind';
import noOutletIcon from 'assets/images/backoffice/online-store/no-outlet-icon.svg';
import { ONLINE_STORE_CHANNEL } from 'utils/onlineStoreUtils';
import TooltipHint from 'components/TooltipHint/TooltipHint';
import { closeOutletModal, selectOutlet } from 'actions/onlineStore';
import styles from './OutletModal.css';

class OutletModal extends React.Component {
  constructor() {
    super();
    this.state = {
      searchValue: '',
      selectedOutlet: null,
      tncAgreed: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.showOutletModal && this.props.showOutletModal) {
      this.setState({ selectedOutlet: null, tncAgreed: false });
    }
  }

  render() {
    const { tncAgreed } = this.state;
    const { fbModalOpened, showOutletModal, channel } = this.props;
    const hasOutlet = this._hasOutlet();
    const selectedOutlet = this._getSelectedOutlet();
    const hasOneOutlet = this._hasOneOutlet();

    const buttonEnabled =
      !fbModalOpened &&
      selectedOutlet &&
      (channel === ONLINE_STORE_CHANNEL.FACEBOOK || tncAgreed);
    const headerText =
      hasOneOutlet && ONLINE_STORE_CHANNEL.GOOGLE
        ? 'Connect to Google'
        : 'Choose Outlet to Connect';

    return (
      <Modal
        show={showOutletModal}
        onHide={this._handleHide}
        backdrop="static"
        keyboard={false}
        dialogClassName={styles['outlet-modal']}
      >
        <ModalHeader title={headerText} />

        {hasOutlet ? this._renderModalBody() : this._renderNoOutletModalBody()}

        <Modal.Footer>
          <button
            className="btn btn-default"
            type="button"
            onClick={this._handleHide}
          >
            {hasOutlet ? 'Cancel' : 'Close'}
          </button>
          {hasOutlet && (
            <button
              className={classNames(
                'btn',
                { 'btn-primary': !!selectedOutlet },
                styles['main-button'],
                channel === ONLINE_STORE_CHANNEL.FACEBOOK &&
                  !!selectedOutlet &&
                  styles['main-button-facebook']
              )}
              type="button"
              disabled={!buttonEnabled}
              onClick={this._handleSelect}
            >
              {channel === ONLINE_STORE_CHANNEL.FACEBOOK
                ? 'Continue with Facebook'
                : 'Connect to Google'}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }

  _hasOutlet = () => {
    const { outlets, shops } = this.props;

    return outlets.some(outlet =>
      shops.every(shop => shop.outlet_id !== outlet.id)
    );
  };

  _hasOneOutlet = () => {
    const { outlets } = this.props;

    return outlets.length === 1 && this._hasOutlet;
  };

  _renderModalBody = () => {
    const { channel } = this.props;

    return (
      <Modal.Body bsClass="modal-body">
        {this._renderTopDescription(channel)}

        {!this._hasOneOutlet() && (
          <div>
            <div>
              <SearchBar
                iconOnLeft
                name="search"
                placeholder="Search Outlet Name"
                input={{ onChange: this._onChangeFilterKeyword }}
                className={styles['search-bar']}
              />
            </div>

            {this._renderOutlets()}
          </div>
        )}

        {this._renderBottomSection(channel)}
      </Modal.Body>
    );
  };

  _renderNoOutletModalBody = () => {
    const { channel } = this.props;
    const channelText =
      channel === ONLINE_STORE_CHANNEL.FACEBOOK
        ? 'Facebook and Instagram Shop'
        : 'Google';

    return (
      <Modal.Body bsClass={`modal-body ${styles['modal-body-no-outlet']}`}>
        <img src={noOutletIcon} />
        <h4>No more available outlets.</h4>
        <p>
          All outlets for your store has been succesfully connected to{' '}
          {channelText}. To add more, create new outlets and connect it again
          from here.
        </p>
      </Modal.Body>
    );
  };

  _renderOutlets = () => {
    const { searchValue } = this.state;
    const { outlets } = this.props;
    const filteredOutlets = outlets
      .filter(outlet => outlet.name.toLowerCase().indexOf(searchValue) !== -1)
      .sort((outletA, outletB) => {
        const shopA = this._getShop(outletA.id);
        const shopB = this._getShop(outletB.id);

        if (!shopA && !shopB) {
          return 0;
        }

        if (!shopA) {
          return -1;
        }

        return 1;
      });

    const outletComponents = filteredOutlets.map(outlet => {
      const shop = this._getShop(outlet.id);

      return (
        <div key={outlet.id} className={`radio radio-primary ${styles.radio}`}>
          <input
            type="radio"
            checked={this._checked(outlet, shop)}
            onChange={() => this._onChangeCheckbox(outlet)}
            id={`outlet-${outlet.id}`}
            disabled={!!shop}
          />
          <label htmlFor={`outlet-${outlet.id}`}>
            <div className={styles['outlet-name']}>
              {outlet.name}
              {!!shop && (
                <span className={styles['shop-status']}>
                  {shop.status === 'ACTIVE' ? 'Active' : 'Incomplete'}
                </span>
              )}
            </div>
            <div className={styles['outlet-description']}>
              <strong>{outlet.city} &#9679; </strong>
              <span>{outlet.address}</span>
            </div>
          </label>
        </div>
      );
    });

    return (
      <div className={styles['outlet-selection-box']}>{outletComponents}</div>
    );
  };

  _renderTopDescription = channel => {
    if (channel === ONLINE_STORE_CHANNEL.FACEBOOK) {
      return (
        <div className={styles['top-description']}>
          <b>Choose one outlet</b> to be connected with{' '}
          <b>Facebook and Instagram Shop</b>.
        </div>
      );
    }

    if (channel === ONLINE_STORE_CHANNEL.GOOGLE) {
      const tooltip = (
        <span>
          Go to{' '}
          <a className={styles.underline} href="/library/list" target="_blank">
            Item Library
          </a>{' '}
          page. When you add or edit an item, make sure all "Mandatory for
          Online Store" label is complete.
        </span>
      );

      return (
        <div className={styles['top-description']}>
          Only connect outlet that is <b>ready for online selling</b> with{' '}
          <b>complete Online Store attributes products</b>{' '}
          <TooltipHint
            placement="bottom"
            tooltip={tooltip}
            className={styles['google-hint-glyph']}
          />{' '}
          to Google.
        </div>
      );
    }

    return null;
  };

  _renderBottomSection = channel => {
    const { tncAgreed } = this.state;

    if (channel === ONLINE_STORE_CHANNEL.FACEBOOK) {
      return (
        <div className={styles.hint}>
          Tips: <strong>Use outlet name</strong> as your Facebook Page &amp;
          Facebook Catalog name so it’s easier to manage.
        </div>
      );
    }

    if (channel === ONLINE_STORE_CHANNEL.GOOGLE) {
      return (
        <div>
          <label className={styles.tnc}>
            <input
              type="checkbox"
              checked={tncAgreed}
              onChange={this._handleTermChanged}
            />
            &nbsp; I have read and agree to the{' '}
            <a
              href="https://merchants.google.com/files/tos/extend/ID/tos.html"
              target="_blank"
              className={styles.underline}
            >
              Terms and Conditions
            </a>
          </label>
        </div>
      );
    }

    return null;
  };

  _getShop = outletId => {
    const { shops } = this.props;
    return shops.filter(s => s.outlet_id === outletId)[0];
  };

  _onChangeFilterKeyword = e => {
    this.setState({
      searchValue: (e.target.value || '').toLowerCase(),
    });
  };

  _handleHide = () => {
    this.setState({ searchValue: '', selectedOutlet: null, tncAgreed: false });
    this.props.dispatch(closeOutletModal());
  };

  _getSelectedOutlet = () => {
    const { outlets, channel } = this.props;

    if (channel === ONLINE_STORE_CHANNEL.GOOGLE && outlets.length === 1) {
      return outlets[0];
    }

    return this.state.selectedOutlet;
  };

  _checked = (outlet, shop) => {
    const selectedOutlet = this._getSelectedOutlet();

    return (selectedOutlet && selectedOutlet.id === outlet.id) || !!shop;
  };

  _onChangeCheckbox = outlet => {
    this.setState({ selectedOutlet: outlet });
  };

  _handleSelect = () => {
    const { source, channel } = this.props;
    const selectedOutlet = this._getSelectedOutlet();

    this.props.dispatch(
      selectOutlet({
        outletId: selectedOutlet.id,
        outletName: selectedOutlet.name,
        source,
        channel,
      })
    );
  };

  _handleTermChanged = e => {
    this.setState({ tncAgreed: e.target.checked });
  };
}

OutletModal.defaultProps = {
  disableConnectButton: false,
  showOutletModal: false,
  onHide: null,
  onSelectOutlet: null,
  outlets: [],
  shops: [],
};

OutletModal.propTypes = {
  channel: PropTypes.string,
  showOutletModal: PropTypes.bool,
  onHide: PropTypes.func,
  onSelectOutlet: PropTypes.func,
  outlets: PropTypes.array,
  shops: PropTypes.array,
};

function mapStateToProps(state) {
  const { outlets } = state.moka.user;
  const { shop, outletModal, facebook } = state.onlineStore;
  const { shops } = shop;

  return {
    outlets,
    shops,
    fbModalOpened: facebook.isModalOpened,
    ...outletModal,
  };
}

function mapDispatchToProps(dispatch) {
  return { dispatch };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OutletModal);
